/**
 * Diagnostic report assembly + export (009). One document = correlation id +
 * device + codecs + latest snapshot + recent event log (+ recording if any).
 * Measurements and identifiers only — never captured audio/video (FR-011).
 * copyReport() and downloadReport() emit identical content and degrade to each
 * other if one browser API is unavailable.
 */
import { collectDeviceInfo, type DeviceInfo } from './deviceInfo.js'
import { snapshot, type DiagnosticSnapshot } from './collector.js'
import { recentLog, type LogEntry } from './logBuffer.js'
import { exportTimeline } from './recorder.js'
import { useRoomStore } from '../room/store.js'

export interface DebugReport {
  correlationId: { peerId: string | null; roomId: string | null }
  capturedAtIso: string
  device: DeviceInfo
  codecs: { send?: string | undefined; recv: string[] }
  snapshot: DiagnosticSnapshot
  recentLog: LogEntry[]
  recording?: DiagnosticSnapshot[] | undefined
}

function roomIdFromUrl(): string | null {
  try {
    const m = window.location.pathname.match(/\/room\/([^/?#]+)/)
    return m && m[1] ? decodeURIComponent(m[1]) : null
  } catch {
    return null
  }
}

export async function buildReport(): Promise<DebugReport> {
  const snap = await snapshot()
  const recording = exportTimeline()

  const sendVideo = snap.streams.find((s) => s.direction === 'send' && s.kind === 'video')
  const recvCodecs = [
    ...new Set(
      snap.streams
        .filter((s) => s.direction === 'recv' && s.kind === 'video' && s.codec)
        .map((s) => s.codec as string),
    ),
  ]

  return {
    correlationId: { peerId: useRoomStore.getState().localPeerId, roomId: roomIdFromUrl() },
    capturedAtIso: new Date().toISOString(),
    device: collectDeviceInfo(),
    codecs: { send: sendVideo?.codec, recv: recvCodecs },
    snapshot: snap,
    recentLog: recentLog(),
    recording: recording.length > 0 ? recording : undefined,
  }
}

export function reportToJson(report: DebugReport): string {
  return JSON.stringify(report, null, 2)
}

function downloadText(text: string): boolean {
  try {
    const blob = new Blob([text], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `webrtc-debug-${new Date().toISOString().replace(/[:.]/g, '-')}.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
    return true
  } catch {
    return false
  }
}

/** Copy the report to the clipboard; falls back to a file download if clipboard is unavailable. */
export async function copyReport(): Promise<boolean> {
  const text = reportToJson(await buildReport())
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return downloadText(text)
  }
}

/** Save the report as a JSON file. */
export async function downloadReport(): Promise<boolean> {
  return downloadText(reportToJson(await buildReport()))
}
